import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  type MetricProgress,
  type StudentProgressResponse,
  useRevisionGetStudentProgress,
} from '@unsw/api/generated'
import { useAuthStore } from '@unsw/store/auth'
import { Button } from '@unsw/ui/components/ui/button'
import { Card, CardContent } from '@unsw/ui/components/ui/card'
import { RadialProgressChart } from '@unsw/ui/components/ui/radial-progress-chart'
import { Lightbulb, Loader2, Mic, Minus, TrendingDown, TrendingUp } from 'lucide-react'

export const Route = createFileRoute('/dashboard/')({
  beforeLoad: () => {
    const state = useAuthStore.getState()
    const user = state.user
    const activeRole = state.activeRole

    // Check if user is admin
    const isAdmin = activeRole === 'admin' || user?.roles?.some((r) => r.name === 'SuperAdmin')
    if (isAdmin) {
      throw redirect({ to: '/dashboard/usage' })
    }

    // Check if user is teacher
    const isTeacher = activeRole === 'teacher' || user?.status === 'TEACHER'
    if (isTeacher) {
      throw redirect({ to: '/dashboard/teacher-feedback' })
    }
  },
  component: DashboardHome,
})

const METRIC_LABELS: Record<string, string> = {
  ideation: 'Ideation',
  development: 'Organisation',
  grammar: 'Grammar',
  vocabulary: 'Vocabulary',
  fluency: 'Fluency',
}

const METRIC_TIPS: Record<string, string> = {
  ideation:
    'Try brainstorming more ideas before speaking. Consider different perspectives on the topic.',
  development: 'Focus on structuring your thoughts with a clear beginning, middle, and end.',
  grammar: 'Practice common sentence structures and pay attention to verb tenses.',
  vocabulary: 'Expand your word bank by learning synonyms and topic-specific terms.',
  fluency: 'Practice speaking at a steady pace. Record yourself and listen back.',
}

function getWeakestMetric(progressData: StudentProgressResponse): { name: string; score: number } {
  const metrics = [
    { name: 'ideation', score: progressData.ideation.latest_attempt_score },
    { name: 'development', score: progressData.development.latest_attempt_score },
    { name: 'grammar', score: progressData.grammar.latest_attempt_score },
    { name: 'vocabulary', score: progressData.vocabulary.latest_attempt_score },
    { name: 'fluency', score: progressData.fluency.latest_attempt_score },
  ]

  // metrics array always has 5 elements, so metrics[0] is always defined
  return metrics.reduce(
    (weakest, current) => (current.score < weakest.score ? current : weakest),
    metrics[0]!,
  )
}

function ImprovementBadge({ improvement }: { improvement: number }) {
  if (improvement > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600">
        <TrendingUp className="h-3 w-3" />+{improvement.toFixed(1)}
      </span>
    )
  }
  if (improvement < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-500">
        <TrendingDown className="h-3 w-3" />
        {improvement.toFixed(1)}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Minus className="h-3 w-3" />
      No change
    </span>
  )
}

function MetricCard({
  name,
  metric,
}: {
  name: string
  metric: MetricProgress
}) {
  const label = METRIC_LABELS[name] || name
  const showFirstAttempt = metric.attempt_count > 1

  return (
    <Card className="bg-white border border-gray-200">
      <CardContent className="p-3 md:pt-4 md:pb-4">
        <div className="flex flex-col items-center">
          {/* Metric Label */}
          <p className="text-xs md:text-sm font-medium text-center mb-1 md:mb-2">{label}</p>

          {/* Radial Chart */}
          <RadialProgressChart
            latestScore={metric.latest_attempt_score}
            firstScore={metric.first_attempt_score}
            maxScore={10}
            latestLabel={metric.latest_attempt_score_str}
            firstLabel={showFirstAttempt ? metric.first_attempt_score_str : undefined}
            size="sm"
            showFirstAttempt={showFirstAttempt}
          />

          {/* Improvement Badge */}
          <div className="mt-2">
            <ImprovementBadge improvement={metric.improvement} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function SpeakingLevelCard({
  speakingLevel,
  progressData,
}: {
  speakingLevel: MetricProgress
  progressData: StudentProgressResponse
}) {
  const navigate = useNavigate()
  const showFirstAttempt = speakingLevel.attempt_count > 1
  const weakestMetric = getWeakestMetric(progressData)

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <CardContent className="px-4 py-4 md:px-10 md:py-6">
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
          {/* Left: Radial Progress Chart */}
          <div className="flex-shrink-0 md:mr-8">
            <RadialProgressChart
              latestScore={speakingLevel.latest_attempt_score}
              firstScore={speakingLevel.first_attempt_score}
              maxScore={10}
              latestLabel={speakingLevel.latest_attempt_score_str}
              firstLabel={showFirstAttempt ? speakingLevel.first_attempt_score_str : undefined}
              size="lg"
              showFirstAttempt={showFirstAttempt}
            />
          </div>

          {/* Middle: Info Section */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-2">Your Speaking Level</h2>
            <p className="text-muted-foreground mb-3">
              Your overall speaking performance, updated with each recording
            </p>

            {/* Score Label */}
            <div className="mb-3">
              <span className="inline-block px-3 py-1 bg-[#DAD7FE] text-black text-sm font-bold font-mono uppercase tracking-wider">
                {speakingLevel.latest_attempt_score_str}
              </span>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 text-sm mb-3">
              <ImprovementBadge improvement={speakingLevel.improvement} />
              <span className="text-muted-foreground">
                {speakingLevel.attempt_count}{' '}
                {speakingLevel.attempt_count === 1 ? 'recording' : 'recordings'}
              </span>
            </div>

            {/* Legend */}
            {showFirstAttempt && (
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3"
                    style={{ background: 'linear-gradient(180deg, #676767 0%, #A7A7A7 100%)' }}
                  />
                  <span className="text-xs text-muted-foreground">First Attempt</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3"
                    style={{ background: 'linear-gradient(180deg, #3f61c4 0%, #7B93D9 100%)' }}
                  />
                  <span className="text-xs text-muted-foreground">Latest Attempt</span>
                </div>
              </div>
            )}
          </div>

          {/* Right: Tips & CTA Section */}
          <div className="flex-shrink-0 w-full md:w-64 space-y-4">
            {/* Quick Tip */}
            {weakestMetric && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-800 mb-1">
                      Tip: Improve your {METRIC_LABELS[weakestMetric.name]}
                    </p>
                    <p className="text-xs text-amber-700">{METRIC_TIPS[weakestMetric.name]}</p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA Button */}
            <Button
              onClick={() => navigate({ to: '/dashboard/recordings' })}
              className="w-full"
              size="lg"
            >
              <Mic className="w-4 h-4 mr-2" />
              Start New Recording
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ColorKeyLegend() {
  return (
    <div className="flex justify-center gap-6 items-center mt-4">
      <div className="flex items-center gap-2">
        <div
          className="w-4 h-4"
          style={{ background: 'linear-gradient(180deg, #676767 0%, #A7A7A7 100%)' }}
        />
        <span className="text-sm text-muted-foreground">First Attempt</span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="w-4 h-4"
          style={{ background: 'linear-gradient(180deg, #3f61c4 0%, #7B93D9 100%)' }}
        />
        <span className="text-sm text-muted-foreground">Latest Attempt</span>
      </div>
    </div>
  )
}

function EmptyState() {
  const navigate = useNavigate()

  return (
    <Card className="mx-auto max-w-md text-center bg-white">
      <CardContent className="pt-8 pb-8">
        <h2 className="mb-2 text-xl font-semibold">No Progress Data Yet</h2>
        <p className="mb-4 text-muted-foreground">
          Start recording your speaking practice to see your progress here.
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: '/dashboard/recordings' })}
          className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Go to Recordings
        </button>
      </CardContent>
    </Card>
  )
}

function DashboardHome() {
  const user = useAuthStore((state) => state.user)
  const { data, isLoading, error } = useRevisionGetStudentProgress(user?.id || 0, {
    query: { enabled: !!user?.id },
  })

  // Extract progress data (handle response structure)
  const progressData: StudentProgressResponse | null = data?.status === 200 ? data.data : null

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-4 md:p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load progress data. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Check if user has any recordings
  if (!progressData || progressData.speaking_level.attempt_count === 0) {
    return (
      <div className="p-4 md:p-8">
        <h1 className="mb-4 md:mb-6 font-heading text-xl md:text-2xl font-bold">My Progress</h1>
        <EmptyState />
      </div>
    )
  }

  const showColorKey = progressData.speaking_level.attempt_count > 1

  return (
    <div className="p-4 md:p-8">
      <h1 className="mb-4 md:mb-6 font-heading text-xl md:text-2xl font-bold">My Progress</h1>

      <div className="space-y-4 md:space-y-6">
        {/* Speaking Level - Featured Card with Radial Chart */}
        <SpeakingLevelCard
          speakingLevel={progressData.speaking_level}
          progressData={progressData}
        />

        {/* Performance Metrics Grid with Radial Charts */}
        <div>
          <h2 className="mb-3 md:mb-4 text-base md:text-lg font-semibold">Performance Breakdown</h2>
          <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <MetricCard name="ideation" metric={progressData.ideation} />
            <MetricCard name="development" metric={progressData.development} />
            <MetricCard name="grammar" metric={progressData.grammar} />
            <MetricCard name="vocabulary" metric={progressData.vocabulary} />
            <MetricCard name="fluency" metric={progressData.fluency} />
          </div>

          {/* Color Key Legend */}
          {showColorKey && <ColorKeyLegend />}
        </div>
      </div>
    </div>
  )
}
